import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';
import { todayStr } from '../lib/format.js';

// Manager-side manual hours entry — for off-clock staff (Roasters,
// occasional helpers, anyone who didn't punch a clock). Creates a
// single-shift punch via the existing manager_create endpoint, with
// audit. Shown next to the Close button on TimecardReviewPanel.
export function ManualHoursButton({ currentUser, onAdded }) {
  const headers = { 'x-user-id': String(currentUser.id) };
  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [form, setForm] = useState({
    user_id: '',
    location_id: '',
    shift_date: todayStr(),
    start_time: '09:00',
    hours: '',
    note: '',
  });

  useEffect(() => {
    if (!open) return;
    api('GET', '/api/users', null, headers).then(rs => setUsers(Array.isArray(rs) ? rs : [])).catch(() => {});
    api('GET', '/api/locations').then(setLocations).catch(() => {});
  }, [open]);

  async function submit(e) {
    e.preventDefault();
    if (!form.user_id || !form.location_id || !form.hours) return;
    const hrs = Number(form.hours);
    if (!(hrs > 0 && hrs <= 24)) { setErr('Hours must be between 0 and 24.'); return; }
    setBusy(true); setErr('');
    try {
      const start = new Date(`${form.shift_date}T${form.start_time}:00`);
      const end = new Date(start.getTime() + hrs * 60 * 60 * 1000);
      await api('POST', '/api/admin/timeclock/punches', {
        user_id: Number(form.user_id),
        location_id: Number(form.location_id),
        clock_in_at: start.toISOString(),
        clock_out_at: end.toISOString(),
        note: form.note || `Manual entry — ${hrs}h`,
      }, headers);
      setOpen(false);
      setForm({ user_id: '', location_id: '', shift_date: todayStr(), start_time: '09:00', hours: '', note: '' });
      onAdded && onAdded();
    } catch (e) { setErr(e.message || 'Save failed'); }
    finally { setBusy(false); }
  }

  return (
    <>
      <button onClick={() => setOpen(true)} className="btn-secondary" style={{ fontSize: 12 }}>
        + Add hours
      </button>
      {open && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 60, padding: 16 }}>
          <form onSubmit={submit}
            style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 480,
                      maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: 16, borderBottom: '1px solid #f0f0f0' }}>
              <div style={{ fontSize: 16, fontWeight: 700 }}>Add hours for off-clock staff</div>
              <div style={{ fontSize: 11, color: '#9e9e9e' }}>
                For roasters, occasional helpers, or anyone who didn't punch a clock. Logged with a manager-create audit row.
              </div>
            </div>
            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <label style={{ fontSize: 12, fontWeight: 600 }}>
                Staff member
                <select value={form.user_id} onChange={e => setForm(f => ({ ...f, user_id: e.target.value }))} required
                  style={{ display: 'block', width: '100%', padding: 8, border: '1px solid #d0d4d9', borderRadius: 6, marginTop: 4 }}>
                  <option value="">— pick a person —</option>
                  {users.map(u => (
                    <option key={u.id} value={u.id}>{u.name} · {u.role}{u.staff_position ? ` · ${u.staff_position}` : ''}</option>
                  ))}
                </select>
              </label>
              <label style={{ fontSize: 12, fontWeight: 600 }}>
                Location
                <select value={form.location_id} onChange={e => setForm(f => ({ ...f, location_id: e.target.value }))} required
                  style={{ display: 'block', width: '100%', padding: 8, border: '1px solid #d0d4d9', borderRadius: 6, marginTop: 4 }}>
                  <option value="">— pick a café —</option>
                  {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              </label>
              <div style={{ display: 'flex', gap: 8 }}>
                <label style={{ fontSize: 12, fontWeight: 600, flex: 1 }}>
                  Date
                  <input type="date" value={form.shift_date} onChange={e => setForm(f => ({ ...f, shift_date: e.target.value }))} required
                    style={{ display: 'block', width: '100%', padding: 8, border: '1px solid #d0d4d9', borderRadius: 6, marginTop: 4 }} />
                </label>
                <label style={{ fontSize: 12, fontWeight: 600, width: 120 }}>
                  Start time
                  <input type="time" value={form.start_time} onChange={e => setForm(f => ({ ...f, start_time: e.target.value }))} required
                    style={{ display: 'block', width: '100%', padding: 8, border: '1px solid #d0d4d9', borderRadius: 6, marginTop: 4 }} />
                </label>
                <label style={{ fontSize: 12, fontWeight: 600, width: 100 }}>
                  Hours
                  {/* JT manager 2026-06-08: 15-minute step forced "1.17 hours
                      actually worked" into "1.25" entered. Switched to 0.01
                      (~36s precision) so the manager can capture exactly
                      what the staffer worked without artificial rounding. */}
                  <input type="number" step="0.01" min="0.01" max="24" value={form.hours}
                    onChange={e => setForm(f => ({ ...f, hours: e.target.value }))} required
                    placeholder="e.g. 1.17"
                    style={{ display: 'block', width: '100%', padding: 8, border: '1px solid #d0d4d9', borderRadius: 6, marginTop: 4 }} />
                </label>
              </div>
              <label style={{ fontSize: 12, fontWeight: 600 }}>
                Note (optional)
                <input type="text" value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))}
                  placeholder="e.g. roasted Tuesday batch"
                  style={{ display: 'block', width: '100%', padding: 8, border: '1px solid #d0d4d9', borderRadius: 6, marginTop: 4 }} />
              </label>
              {err && <div style={{ fontSize: 12, color: '#c62828' }}>{err}</div>}
            </div>
            <div style={{ padding: 12, borderTop: '1px solid #e0e0e0', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setOpen(false)} className="btn-secondary">Cancel</button>
              <button type="submit" disabled={busy} className="btn-primary">
                {busy ? 'Saving…' : 'Add hours'}
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}
